import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  alpha,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Paper,
  Tooltip,
  Checkbox,
  ListItemIcon,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import api from "../../../../../services/api";
import { HOST_TYPES } from "../../../../../utils/hostTypes";
import useClientesPaginados from "../../../../../components/clientesPaginados/useClientesPaginados";
import ErpChip from "../../../../../components/erp/ErpChip";

// Mesmo recorte de /interno/find/clientsOnlyWithHost
const FILTRO_CLIENTES = { comHost: true };

// ─── Detecta tipo baseado nos grupos do Zabbix e na funcao do host ────────────
function detectHostType(groups = [], funcao = "", modelo = "") {
  const funcaoUp = (funcao || "").toUpperCase().trim();
  const modeloUp = (modelo || "").toUpperCase().trim();
  const groupNames = (groups || []).map((g) =>
    (g?.name || "").toLowerCase()
  );

  const OLT_NAMES = [
    "olt fiberhome",
    "olt datacom",
    "olt huawei",
    "olt zte",
    "olt nokia",
    "olt phyhome",
  ];

  // ONU: host pertence a um grupo de OLT
  if (groupNames.some((n) => OLT_NAMES.some((olt) => n.includes(olt)))) {
    return "onu";
  }

  // ONU: funcao ou modelo explícitos
  if (funcaoUp.includes("ONU") || modeloUp.includes("ONU")) {
    return "onu";
  }

  // Sessão BGP
  if (
    funcaoUp.includes("BGP") ||
    funcaoUp.includes("SESSION") ||
    funcaoUp.includes("SESSAO") ||
    groupNames.some((n) => n.includes("bgp") || n.includes("session"))
  ) {
    return "session";
  }

  // Link dedicado
  if (
    funcaoUp.includes("LINK") ||
    groupNames.some((n) => n.includes("link") && !n.includes("links de"))
  ) {
    return "link";
  }

  // CPE explícito ou default
  return "cpe";
}

// ─── Formatador de CNPJ/CPF ──────────────────────────────────────────────────
const formatCpfCnpj = (value) => {
  if (!value) return "";
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (cleaned.length === 14) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return value;
};

// ─── Componente Principal ─────────────────────────────────────────────────────
export default function VincularClientesStep({
  hostId,
  groupId = null,
  hostGroups = [],
  hostFuncao = "",
  hostModelo = "",
  hostClientsMap,
  setHostClientsMap,
  pendingMode = false,
  pendingLinks = [],
  setPendingLinks = () => {},
}) {
  const theme = useTheme();
  const {
    busca: search,
    setBusca: setSearch,
    linhas: clients,
    carregando: loading,
    opcoesGrid,
  } = useClientesPaginados(FILTRO_CLIENTES, { tamanhoInicial: 5 });

  // Dialog state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientServices, setClientServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [searchService, setSearchService] = useState("");

  const filteredClientServices = useMemo(() => {
    if (!searchService) return clientServices;
    const lower = searchService.toLowerCase();
    return clientServices.filter((service) => {
      const title = service?.nome || service?.descricao || "";
      const id = String(service?.id_cliente_servico || service?.id || service?.idContrato || service?.contratoId || "");
      return title.toLowerCase().includes(lower) || id.toLowerCase().includes(lower);
    });
  }, [clientServices, searchService]);

  // Tipo detectado automaticamente — pode ser sobrescrito pelo usuário
  const detectedType = useMemo(
    () => detectHostType(hostGroups, hostFuncao, hostModelo),
    [hostGroups, hostFuncao, hostModelo]
  );
  const [selectedType, setSelectedType] = useState(detectedType);

  // Sincroniza quando o host mudar (modal reabre com host diferente)
  useEffect(() => {
    setSelectedType(detectHostType(hostGroups, hostFuncao, hostModelo));
  }, [hostGroups, hostFuncao, hostModelo]);

  // Already linked clients for this host
  const linkedClients = useMemo(() => {
    if (pendingMode) return pendingLinks;
    return hostClientsMap?.[hostId] || [];
  }, [hostId, hostClientsMap, pendingMode, pendingLinks]);

  const handleLinkClick = async (client) => {
    setSelectedClient(client);
    setOpenServiceDialog(true);
    setLoadingServices(true);
    setSelectedServices([]);
    setSearchService("");
    try {
      // clienteId: o backend busca no ERP do próprio cliente — CNPJ e código
      // se repetem entre ERPs e trariam serviços de outro cliente.
      const response = await api.post("/interno/find/allServiceClient", {
        clienteId: client.id,
        cnpj: client.cnpj,
        idClienteErp: client.clienteErpId,
      });
      setClientServices(
        Array.isArray(response.data) ? response.data : [response.data]
      );
    } catch (error) {
      console.error("Erro ao buscar serviços do cliente:", error);
      setClientServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const confirmMultipleLinks = async (services) => {
    try {
      const type = selectedType;
      const linksToAdd = [];

      await Promise.all(
        services.map(async (service) => {
          const servicoId = String(
            service?.id_cliente_servico ||
              service?.id ||
              service?.idContrato ||
              service?.contratoId ||
              Date.now()
          );

          if (!pendingMode) {
            await api.post("/zabbix/hosts/linkClient", {
              action: "link",
              hostId,
              groupId,
              clienteId: selectedClient.id,
              servicoId,
              type,
            });
          }

          linksToAdd.push({
            clienteId: selectedClient.id,
            clientName: selectedClient.nome,
            erp: selectedClient.erp ?? null,
            servicoId,
            type,
          });
        })
      );

      // Atualiza estado local de uma vez
      if (pendingMode) {
        setPendingLinks((prev) => [...prev, ...linksToAdd]);
      } else {
        setHostClientsMap((prev) => {
          const currentLinks = prev[hostId] || [];
          return {
            ...prev,
            [hostId]: [...currentLinks, ...linksToAdd],
          };
        });
      }
    } catch (error) {
      console.error("Erro ao vincular cliente(s):", error);
    } finally {
      setOpenServiceDialog(false);
      setSelectedClient(null);
      setSelectedServices([]);
    }
  };

  const handleUnlinkClick = async (client) => {
    try {
      const link = linkedClients.find((l) => l.clienteId === client.id);
      if (!link) return;

      if (!pendingMode) {
        await api.post("/zabbix/hosts/linkClient", {
          action: "unlink",
          hostId,
          clienteId: client.id,
          servicoId: link.servicoId,
        });

        setHostClientsMap((prev) => {
          const currentLinks = prev[hostId] || [];
          return {
            ...prev,
            [hostId]: currentLinks.filter(
              (l) => l.clienteId !== client.id || l.servicoId !== link.servicoId
            ),
          };
        });
      } else {
        setPendingLinks((prev) => prev.filter(
          (l) => l.clienteId !== client.id || l.servicoId !== link.servicoId
        ));
      }
    } catch (error) {
      console.error("Erro ao desvincular cliente:", error);
    }
  };

  const typeConfig = HOST_TYPES.find((t) => t.value === selectedType) || HOST_TYPES[0];

  const columns = [
    {
      field: "nome",
      headerName: "Clientes   ",
      flex: 1,
    },
    // O mesmo cliente/código pode existir em ERPs diferentes.
    {
      field: "erp",
      headerName: "ERP",
      width: 110,
      sortable: false,
      renderCell: (params) => <ErpChip erp={params.row?.erp} />,
    },
    {
      field: "cnpj",
      headerName: "CNPJ / CPF",
      width: 160,
      renderCell: (params) => formatCpfCnpj(params.value || params.row.cnpj)
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const link = linkedClients.find((l) => l.clienteId === params.row.id);
        if (!link) {
          return (
            <Chip
              label="Não Vinculado"
              color="default"
              size="small"
              variant="outlined"
            />
          );
        }
        const linkType = HOST_TYPES.find((t) => t.value === link.type);
        return (
          <Chip
            label={`Vinculado`}
            color={linkType?.color || "success"}
            size="small"
            variant="filled"
            icon={linkType?.icon}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const isLinked = linkedClients.some(
          (l) => l.clienteId === params.row.id
        );
        return isLinked ? (
          <Button
            size="small"
            color="error"
            startIcon={<LinkOffIcon />}
            onClick={() => handleUnlinkClick(params.row)}
          >
            Desvincular
          </Button>
        ) : (
          <Button
            size="small"
            color="primary"
            startIcon={<LinkIcon />}
            onClick={() => handleLinkClick(params.row)}
          >
            Vincular
          </Button>
        );
      },
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* ─── Banner: Tipo detectado ─── */}
      {/* <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette[typeConfig.color]?.main || theme.palette.primary.main, 0.35)}`,
          backgroundColor: alpha(
            theme.palette[typeConfig.color]?.main || theme.palette.primary.main,
            0.06
          ),
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            color:
              theme.palette[typeConfig.color]?.main ||
              theme.palette.primary.main,
          }}
        >
          {typeConfig.icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Tipo detectado:{" "}
            <span
              style={{
                color:
                  theme.palette[typeConfig.color]?.main ||
                  theme.palette.primary.main,
              }}
            >
              {typeConfig.label}
            </span>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {typeConfig.description}
          </Typography>
        </Box>
        <Tooltip title="O tipo é detectado automaticamente pelos grupos do Zabbix. Você pode alterá-lo antes de vincular.">
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: "text.disabled", cursor: "help" }}
          />
        </Tooltip>
      </Paper> */}

      {/* ─── Busca e Tabela ─── */}
      <TextField
        label="Buscar Cliente"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mt: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <DataGrid
        rows={clients}
        columns={columns}
        loading={loading}
        pageSizeOptions={[5, 10, 25]}
        {...opcoesGrid}
        disableRowSelectionOnClick
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }}
      />

      {/* ─── Dialog: Seleção de Serviço e Tipo ─── */}
      <Dialog
        open={openServiceDialog}
        onClose={() => setOpenServiceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Selecionar Serviço
          {/* Serviços do ERP deste cliente */}
          <ErpChip erp={selectedClient?.erp} />
        </DialogTitle>
        <DialogContent dividers>
          {/* Seletor de Tipo */}
          {/* <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Tipo de vínculo
            </Typography>
            <ToggleButtonGroup
              value={selectedType}
              exclusive
              onChange={(_, val) => val && setSelectedType(val)}
              size="small"
              fullWidth
              sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}
            >
              {HOST_TYPES.map((t) => (
                <ToggleButton
                  key={t.value}
                  value={t.value}
                  sx={{
                    flex: "1 0 auto",
                    borderRadius: "8px !important",
                    border: `1px solid ${theme.palette.divider} !important`,
                    gap: 0.5,
                    "&.Mui-selected": {
                      backgroundColor: alpha(
                        theme.palette[t.color]?.main || theme.palette.primary.main,
                        0.12
                      ),
                      color:
                        theme.palette[t.color]?.main ||
                        theme.palette.primary.main,
                      borderColor: `${theme.palette[t.color]?.main || theme.palette.primary.main} !important`,
                    },
                  }}
                >
                  {t.icon}
                  {t.label}
                  {t.value === detectedType && (
                    <Chip
                      label="auto"
                      size="small"
                      sx={{ height: 16, fontSize: 10, ml: 0.5 }}
                    />
                  )}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {HOST_TYPES.find((t) => t.value === selectedType)?.description}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} /> */}

          {/* Lista de Serviços */}
          {/* <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Serviço do cliente
          </Typography> */}

          {loadingServices ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : clientServices.length > 0 ? (
            <>
              <TextField
                label="Buscar Serviço"
                variant="outlined"
                size="small"
                fullWidth
                sx={{  mt: 1 }}
                value={searchService}
                onChange={(e) => setSearchService(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              {filteredClientServices.length > 0 ? (
                <List sx={{ mt: 0.75 }}>
                  {filteredClientServices.map((service, index) => {
                    const title =
                      service?.nome ||
                      service?.descricao ||
                      `Serviço ${index + 1}`;
                    const id =
                      service?.id_cliente_servico ||
                      service?.id ||
                      service?.idContrato ||
                      service?.contratoId;
                    return (
                      <ListItem
                        key={index}
                        button
                        onClick={() => {
                          const isSelected = selectedServices.some(s => s === service);
                          if (isSelected) {
                            setSelectedServices(selectedServices.filter(s => s !== service));
                          } else {
                            setSelectedServices([...selectedServices, service]);
                          }
                        }}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          mb: 1,
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedServices.some(s => s === service)}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={title}
                          secondary={id ? `ID: ${id}` : "Sem ID visível"}
                        />
                        {/* <Chip
                          label={typeConfig.label}
                          size="small"
                          color={typeConfig.color}
                          variant="outlined"
                          icon={typeConfig.icon}
                          sx={{ ml: 1 }}
                        /> */}
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                  Nenhum serviço encontrado na busca.
                </Typography>
              )}
            </>
          ) : (
            <Typography
              color="text.secondary"
              align="center"
              sx={{ py: 3 }}
            >
              Nenhum serviço encontrado para este cliente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenServiceDialog(false)}>Cancelar</Button>
          {clientServices.length > 0 && (
            <Button
              color="primary"
              variant="contained"
              disabled={selectedServices.length === 0}
              onClick={() => confirmMultipleLinks(selectedServices)}
            >
              Vincular Selecionados ({selectedServices.length})
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
